// Cola de batching para page views + heartbeats de sesión.
// Fase 2: agrupa eventos y los manda con sendBeacon (no bloquea unload) o fetch keepalive como fallback.
//
// Reusa appConfig.apiUrl (mismo cliente HTTP que el resto del frontend, ver Z:\src\data\config.js).
// No usa apiFetch porque Beacon API no soporta headers personalizados ni promesas de respuesta.

import appConfig from "../data/config";

const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5000;

let pageviewQueue = [];
let flushTimer = null;

function getPageviewUrl() {
  return `${appConfig.apiUrl}/tracking/pageview-batch`;
}

function getHeartbeatUrl() {
  return `${appConfig.apiUrl}/tracking/session/heartbeat`;
}

/**
 * Envía un payload JSON usando sendBeacon si está disponible; si falla o no existe,
 * cae a fetch con keepalive (sobrevive un poco más allá del unload en navegadores modernos).
 */
function sendPayload(url, payload) {
  const json = JSON.stringify(payload);

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([json], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    } catch {
      // sigue al fallback de fetch
    }
  }

  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: json,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // analytics no debe romper la navegación
  }
}

/**
 * Vacía la cola de page views actual (si tiene elementos) y la manda en un solo batch.
 */
export function flushPageviews() {
  if (pageviewQueue.length === 0) return;

  const events = pageviewQueue.splice(0, MAX_BATCH_SIZE);
  sendPayload(getPageviewUrl(), { events });

  // Si quedaron eventos pendientes (cola superó MAX_BATCH_SIZE), se mandan en el próximo flush.
}

/**
 * Encola un page view. Si se alcanza el tamaño máximo de batch, flushea inmediatamente.
 */
export function enqueuePageview(event) {
  pageviewQueue.push(event);

  if (pageviewQueue.length >= MAX_BATCH_SIZE) {
    flushPageviews();
  }
}

/**
 * Manda un heartbeat de sesión (fire-and-forget, no necesita batching propio).
 */
export function sendHeartbeat(sessionUuid, seconds = 30) {
  sendPayload(getHeartbeatUrl(), { session_uuid: sessionUuid, seconds });
}

/**
 * Arranca el flush periódico (cada FLUSH_INTERVAL_MS) y los listeners de cierre/ocultamiento de tab.
 * Idempotente: si ya está corriendo, no hace nada.
 */
export function startQueueFlusher() {
  if (flushTimer) return;

  flushTimer = setInterval(flushPageviews, FLUSH_INTERVAL_MS);

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushPageviews();
      }
    });
  }

  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", flushPageviews);
  }
}

/**
 * Detiene el flush periódico (uso en tests / cleanup).
 */
export function stopQueueFlusher() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}
